/**
 * OrderService
 * Service for Order entity operations
 * Route: api/orders
 * Controller: OrdersController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * OrderService
 * Handles order CRUD operations and order-specific queries.
 * Backend now returns OrderModelView (enriched) instead of raw Order entities.
 * Maps to /api/orders endpoint
 */
export class OrderService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? OrderService.DEFAULT_SERVICE_PATH,
            basePath: OrderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // Override getById to return OrderModelView
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Order with ID ${id} not found`);
        }
        return response.data;
    }
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== FILTER QUERIES ====================
    /**
     * Get filtered orders
     * GET /api/orders?statusId={statusId}&locationId={locationId}...
     */
    async getFiltered(filter) {
        const response = await this.getFilteredRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered orders (raw)
     */
    getFilteredRaw(filter) {
        const params = new URLSearchParams();
        if (filter.statusID !== undefined)
            params.append('statusId', String(filter.statusID));
        if (filter.locationID !== undefined)
            params.append('locationId', String(filter.locationID));
        if (filter.eventID !== undefined)
            params.append('eventId', String(filter.eventID));
        if (filter.isProcessed !== undefined)
            params.append('isProcessed', String(filter.isProcessed));
        if (filter.referenceNo)
            params.append('referenceNo', filter.referenceNo);
        if (filter.dateFrom)
            params.append('dateFrom', filter.dateFrom instanceof Date ? filter.dateFrom.toISOString() : filter.dateFrom);
        if (filter.dateTo)
            params.append('dateTo', filter.dateTo instanceof Date ? filter.dateTo.toISOString() : filter.dateTo);
        const url = this.buildUrl(`?${params.toString()}`);
        return this.http.get(url);
    }
    // ==================== STORE ADMIN — list view + aggregates ====================
    //
    // These paginate and aggregate server-side. Prefer them over getFiltered(), which
    // returns the entire matching set unpaginated.
    //
    // Every one takes the same filter, so a dashboard and the grid beneath it always
    // agree on what they are describing.
    /**
     * Filtered, sorted, paged orders with a total count.
     * POST /api/orders/list-view
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return (response.data ?? { items: [], totalCount: 0, skip: 0, take: 0, hasMore: false });
    }
    listViewRaw(filter) {
        return this.http.post(this.buildUrl('list-view'), filter ?? {});
    }
    /**
     * Headline order and revenue figures.
     * POST /api/orders/stats/overview
     */
    async statsOverview(filter) {
        const response = await this.statsOverviewRaw(filter);
        return response.data ?? null;
    }
    statsOverviewRaw(filter) {
        return this.http.post(this.buildUrl('stats/overview'), filter ?? {});
    }
    /**
     * Order counts and value grouped by status.
     * POST /api/orders/stats/by-status
     */
    async statsByStatus(filter) {
        const response = await this.statsByStatusRaw(filter);
        return response.data ?? [];
    }
    statsByStatusRaw(filter) {
        return this.http.post(this.buildUrl('stats/by-status'), filter ?? {});
    }
    /**
     * Order counts and value grouped by location.
     * POST /api/orders/stats/by-location
     */
    async statsByLocation(filter) {
        const response = await this.statsByLocationRaw(filter);
        return response.data ?? [];
    }
    statsByLocationRaw(filter) {
        return this.http.post(this.buildUrl('stats/by-location'), filter ?? {});
    }
    /**
     * Monthly orders-and-revenue trend, oldest first. Quiet months come back as zeroes,
     * so the line keeps its true shape.
     * POST /api/orders/stats/monthly?months={months}
     */
    async statsMonthly(months = 12, filter) {
        const response = await this.statsMonthlyRaw(months, filter);
        return response.data ?? [];
    }
    statsMonthlyRaw(months = 12, filter) {
        const url = this.buildUrl(`stats/monthly?months=${months}`);
        return this.http.post(url, filter ?? {});
    }
    /**
     * Best-selling products across the filtered orders.
     * POST /api/orders/stats/top-products?take={take}
     */
    async statsTopProducts(take = 10, filter) {
        const response = await this.statsTopProductsRaw(take, filter);
        return response.data ?? [];
    }
    statsTopProductsRaw(take = 10, filter) {
        const url = this.buildUrl(`stats/top-products?take=${take}`);
        return this.http.post(url, filter ?? {});
    }
    /**
     * Quantity sold per size across the filtered orders.
     * POST /api/orders/stats/size-distribution
     */
    async statsSizeDistribution(filter) {
        const response = await this.statsSizeDistributionRaw(filter);
        return response.data ?? [];
    }
    statsSizeDistributionRaw(filter) {
        return this.http.post(this.buildUrl('stats/size-distribution'), filter ?? {});
    }
    /**
     * Move many orders to one status at once. Partial success is normal and reported
     * per order — inspect `results`, not just `succeeded`.
     * POST /api/orders/bulk-status
     */
    async bulkStatus(request) {
        const response = await this.bulkStatusRaw(request);
        return response.data ?? { succeeded: 0, failed: 0, results: [] };
    }
    bulkStatusRaw(request) {
        return this.http.post(this.buildUrl('bulk-status'), request);
    }
    /**
     * Record a payment taken outside PayFast — cash at the counter, or a manual EFT.
     * Advances the order to Paid the same way a settled gateway payment does.
     * POST /api/orders/{orderId}/payments/manual
     */
    async recordManualPayment(orderId, request) {
        const response = await this.recordManualPaymentRaw(orderId, request);
        return response.data ?? null;
    }
    recordManualPaymentRaw(orderId, request) {
        return this.http.post(this.buildUrl(`${orderId}/payments/manual`), request);
    }
    /**
     * Replace every line on a Pending order and recalculate its totals.
     *
     * Rejected with 409 once the order has moved past Pending or carries a settled
     * payment — at that point the lines are what the customer paid for.
     * PUT /api/orders/{orderId}/lines
     */
    async replaceLines(orderId, request) {
        const response = await this.replaceLinesRaw(orderId, request);
        return response.data ?? null;
    }
    replaceLinesRaw(orderId, request) {
        return this.http.put(this.buildUrl(`${orderId}/lines`), request);
    }
}
OrderService.DEFAULT_SERVICE_PATH = 'orders';
OrderService.BASE_PATH = 'api/orders';
//# sourceMappingURL=OrderService.js.map